import { getT } from '@/i18n';
/**
 * A CELLASZÍN-PALETTA — a felhasználó választott területszíne.
 *
 * KÖZÖS kliens/szerver modul: a profil szerkesztője, a térkép és a Firestore-
 * szabály ugyanebből a listából dolgozik. A KULCS tárolódik, nem a hexkód,
 * ezért a paletta árnyalatai adatmigráció nélkül finomhangolhatók.
 *
 * A kulcsok kompatibilitási okból maradnak stabilak. A látható színt és a
 * magyar címkét az itt lévő paletta határozza meg.
 */

/** A 16 alapszín — mindenkinek jár. A sorrend a hexagonpaletta sorrendje. */
export const FREE_CELL_COLORS = {
  purple: { hex: '#DDC3A1', get label() { return getT().colorNames.beige; } },
  violet: { hex: '#E1A344', get label() { return getT().colorNames.goldenBrown; } },
  indigo: { hex: '#D1712F', get label() { return getT().colorNames.copper; } },
  blue: { hex: '#BD505C', get label() { return getT().colorNames.raspberry; } },
  sky: { hex: '#E06E70', get label() { return getT().colorNames.coral; } },
  cyan: { hex: '#CB5043', get label() { return getT().colorNames.brick; } },
  teal: { hex: '#8F3A40', get label() { return getT().colorNames.burgundy; } },
  green: { hex: '#76462D', get label() { return getT().colorNames.walnut; } },
  lime: { hex: '#566F49', get label() { return getT().colorNames.olive; } },
  yellow: { hex: '#418D7A', get label() { return getT().colorNames.turquoise; } },
  amber: { hex: '#315F89', get label() { return getT().colorNames.steel; } },
  orange: { hex: '#5B4A69', get label() { return getT().colorNames.plum; } },
  red: { hex: '#2D5653', get label() { return getT().colorNames.petrol; } },
  rose: { hex: '#709EAA', get label() { return getT().colorNames.slate; } },
  pink: { hex: '#7F7F7F', get label() { return getT().colorNames.gray; } },
  slate: { hex: '#2E2E2E', get label() { return getT().colorNames.anthracite; } },
} as const;

/** A 12 prémium szín — csak Pro-előfizetéssel választható. */
export const PRO_CELL_COLORS = {
  'electric-blue': { hex: '#2879FD', get label() { return getT().colorNames.electricBlue; } },
  aqua: { hex: '#00E4FE', get label() { return getT().colorNames.neonCyan; } },
  ice: { hex: '#01FEA9', get label() { return getT().colorNames.neonMint; } },
  coral: { hex: '#FF6000', get label() { return getT().colorNames.neonOrange; } },
  gold: { hex: '#FFD502', get label() { return getT().colorNames.neonGold; } },
  'acid-green': { hex: '#E3FF00', get label() { return getT().colorNames.acidGreen; } },
  'neon-green': { hex: '#01FF1F', get label() { return getT().colorNames.neonGreen; } },
  'neon-red': { hex: '#FD012F', get label() { return getT().colorNames.neonRed; } },
  'hot-pink': { hex: '#FF00A8', get label() { return getT().colorNames.neonPink; } },
  'deep-green': { hex: '#027501', get label() { return getT().colorNames.deepGreen; } },
  'deep-indigo': { hex: '#0D034D', get label() { return getT().colorNames.deepIndigo; } },
  'electric-purple': { hex: '#7C00FF', get label() { return getT().colorNames.neonPurple; } },
} as const;

export type FreeCellColor = keyof typeof FREE_CELL_COLORS;
export type ProCellColor = keyof typeof PRO_CELL_COLORS;
export type CellColor = FreeCellColor | ProCellColor;

export const CELL_COLORS = { ...FREE_CELL_COLORS, ...PRO_CELL_COLORS } as const;

export const FREE_CELL_COLOR_KEYS = Object.keys(FREE_CELL_COLORS) as FreeCellColor[];
export const PRO_CELL_COLOR_KEYS = Object.keys(PRO_CELL_COLORS) as ProCellColor[];

/** Az alapértelmezett szín a normál paletta legfelső cellája. */
export const DEFAULT_CELL_COLOR: FreeCellColor = 'purple';

/**
 * ⚠️ `Object.hasOwn`, NEM az `in` operátor.
 *
 * Az `in` a prototípus-láncot is nézi, ezért például a `toString` tévesen
 * érvényes színnek számítana.
 */
export function isCellColor(value: unknown): value is CellColor {
  return typeof value === 'string' && Object.hasOwn(CELL_COLORS, value);
}

export function isProCellColor(value: unknown): value is ProCellColor {
  return typeof value === 'string' && Object.hasOwn(PRO_CELL_COLORS, value);
}

/** Ismeretlen vagy hiányzó értéknél az alapértelmezett színre esik vissza. */
export function cellColorHex(value: unknown): string {
  return isCellColor(value) ? CELL_COLORS[value].hex : CELL_COLORS[DEFAULT_CELL_COLOR].hex;
}

/**
 * A hex CSAK akkor, ha a felhasználó ténylegesen választott — különben `null`.
 *
 * ⚠️ NEM ugyanaz, mint a `cellColorHex()`. Ott a hiányzó érték az
 * alapértelmezett palettaszínt adja, ami a térképen helyes (valamit rajzolni
 * kell), de ott hazugság lenne, ahol a „nem választott" eset MÁS
 * megjelenítést kap — például a rivális-sávnál, amely ilyenkor a saját, régi
 * lila-magenta párosát tartja meg (Geri kérése, 2026-08-29).
 */
export function cellColorHexOrNull(value: unknown): string | null {
  return isCellColor(value) ? CELL_COLORS[value].hex : null;
}

/** A választható színek egy adott előfizetési szinten. */
export function availableCellColors(isPro: boolean): CellColor[] {
  return isPro ? [...FREE_CELL_COLOR_KEYS, ...PRO_CELL_COLOR_KEYS] : [...FREE_CELL_COLOR_KEYS];
}
